/**
 * Svelte 5 runes-based global appState (§5.5).
 *
 * Components import `appState` and mutate its properties directly.
 * Derived values use `$derived(…)`; side effects use `$effect(…)`.
 */

import type { Chat, Message, Visitor, WidgetConfig } from './types';

export interface UploadItem {
  id: string;           // local uuid
  name: string;
  size: number;
  progress: number;     // 0..1
  status: 'pending' | 'uploading' | 'done' | 'error';
  error: string | null; // error code when status === 'error'
}

export const appState = $state({
  /** Widget UI */
  isMinimized: true,
  isConnected: false,
  isAuthing: false,
  isMobile: false,

  /** Auth + session */
  publicKey: '' as string,
  zIndex: 9999 as number,
  token: null as string | null,
  cookieId: null as string | null,

  /** Core data */
  chat: null as Chat | null,
  messages: [] as Message[],
  visitor: {
    name: '',
    email: '',
    cleared_at: null,
  } as Visitor,
  widgetConfig: null as WidgetConfig | null,

  /** Transient UI flags */
  welcomePopup: {
    visible: false,
    dismissed: false,
  },
  error: null as string | null,
  unreadCount: 0,
  /** True while waiting for an AI reply after a visitor send. */
  aiThinking: false,
  /**
   * Mirrors server-side `chat.ai_paused_by`. Non-null means AI is not
   * answering right now (either escalated to a human — "system" — or
   * currently taken over by an agent — agent_id). Widget uses this to
   * suppress the "AI Thinking…" indicator when the visitor sends a new
   * message during human support.
   */
  aiPausedBy: null as string | null,
  /**
   * Concurrent upload queue. Each entry tracks the progress + status of
   * one file. Entries auto-clear from the visible list a few seconds after
   * `done`; `error` entries stay until the user dismisses them.
   */
  uploads: [] as UploadItem[],
});

export function resetState(): void {
  appState.isMinimized = true;
  appState.isConnected = false;
  appState.isAuthing = false;
  appState.chat = null;
  appState.messages = [];
  appState.visitor = { name: '', email: '', cleared_at: null };
  appState.widgetConfig = null;
  appState.welcomePopup = { visible: false, dismissed: false };
  appState.error = null;
  appState.unreadCount = 0;
  appState.aiThinking = false;
  appState.aiPausedBy = null;
  appState.uploads = [];
  appState.token = null;
  appState.cookieId = null;
}
